/**
 * ORDER LINE ARITHMETIC — one convention, the server's.
 *
 * de-order-service `logics/order/create.ts` and its
 * `logics/order/helpers/discount-allocation.ts` are the only place these numbers
 * are decided; every screen here is a view of what that code stored. Prices on
 * the order path are VAT-INCLUSIVE, so VAT is EXTRACTED out of the price and is
 * never added on top of a line total:
 *
 *   line net total = price * quantity - the line's own discount   (floored at 0)
 *   line VAT       = net * p / (100 + p)          only when vatType is STANDARD_RATED
 *   line city tax  = net * taxPercent / 100       only when the variant is taxed
 *   ORDER_AMOUNT   = SUM(line net totals) + SUM(additional-line totals)
 *                    — no VAT, no city tax, no shipping fee
 *   grand total    = ORDER_AMOUNT - order-level discount + shipping fee
 *
 * Two defects these functions exist to stop coming back (DSC-001):
 *
 *  1. `price / (vatPercent + 1)` was used across the order screens as the
 *     extracted VAT. It equals the correct `price * p / (100 + p)` only at
 *     p = 10 (solving 1/(p+1) = p/(100+p) gives p = 10) and diverges at every
 *     other rate.
 *  2. `lineTotal + vat + tax` counted the VAT twice, because `lineTotal`
 *     already contains it.
 *
 * `vatPercent` / `taxPercent` are PERCENTS — 10 means 10%, matching
 * create.ts:437-450. They are never fractions.
 *
 * KNOWN, DELIBERATE DIVERGENCE: a line-level AMOUNT discount is treated here as
 * per unit (`quantity * discountValue`), which is what every order screen in
 * this app has always displayed, while create.ts:459 applies it as a flat sum
 * per line. That gap predates the payment-term-discount work and is left alone
 * on purpose — closing it moves the discount column on screens this change does
 * not otherwise touch.
 *
 * Pure and dependency-free so it can be exercised without a browser.
 */

export type IOrderLine = {
  price?: number | null
  quantity?: number | null
  discountType?: string | null
  discountValue?: number | null
  vatType?: string | null
  vatPercent?: number | null
  hasTax?: boolean | null
  taxPercent?: number | null
}

/** The one VAT type whose lines actually carry VAT; every other type is 0. */
export const STANDARD_RATED = "STANDARD_RATED"

/** create.ts:438 — `variant.vatPercent || 10`, so a variant that omits it is a 10% line. */
export const DEFAULT_VAT_PERCENT = 10

const num = (value: unknown): number => Number(value) || 0

/** price * quantity, before any discount. */
export const lineGrossAmount = (line?: IOrderLine | null): number =>
  num(line?.price) * num(line?.quantity)

/** The line's OWN discount in money (not the order-level one). */
export const lineDiscountAmount = (line?: IOrderLine | null): number => {
  const value = num(line?.discountValue)
  if (!line?.discountType || !value) return 0

  if (line.discountType === "PERCENTAGE") return (lineGrossAmount(line) * value) / 100
  if (line.discountType === "AMOUNT") return num(line?.quantity) * value

  // Unknown type: the server's switch has a `default:` that leaves the
  // discount at 0, so nothing is deducted here either.
  return 0
}

/** price * quantity - the line's own discount. This is the line's share of ORDER_AMOUNT. */
export const lineNetAmount = (line?: IOrderLine | null): number =>
  Math.max(0, lineGrossAmount(line) - lineDiscountAmount(line))

/**
 * VAT extracted OUT of `base` (default: the line's net total).
 *
 * Pass `base` to account for an allocated order-level discount share, the way
 * `discount-allocation.ts` recomputes VAT from the post-allocation total.
 */
export const lineVatAmount = (line?: IOrderLine | null, base?: number): number => {
  const vatType = line?.vatType || STANDARD_RATED
  if (vatType !== STANDARD_RATED) return 0

  const percent = num(line?.vatPercent) || DEFAULT_VAT_PERCENT
  if (percent <= 0) return 0

  const total = base === undefined ? lineNetAmount(line) : num(base)
  if (total <= 0) return 0

  return (total * percent) / (100 + percent)
}

/**
 * City tax (НХАТ) on `base` (default: the line's net total).
 *
 * `hasTax === false` means the variant is not taxed at all. Absent means "no
 * opinion" and the percent decides, which is what the allocator's
 * `hasTax !== false` guard ends up storing.
 */
export const lineTaxAmount = (line?: IOrderLine | null, base?: number): number => {
  if (line?.hasTax === false) return 0

  const percent = num(line?.taxPercent)
  if (percent <= 0) return 0

  const total = base === undefined ? lineNetAmount(line) : num(base)
  if (total <= 0) return 0

  return (total * percent) / 100
}

/** Per-unit VAT — the shape `de_order.line.vat_amount` stores. */
export const unitVatAmount = (line?: IOrderLine | null, base?: number): number => {
  const quantity = num(line?.quantity)
  return quantity > 0 ? lineVatAmount(line, base) / quantity : 0
}

/** Per-unit city tax — the shape `de_order.line.tax_amount` stores. */
export const unitTaxAmount = (line?: IOrderLine | null, base?: number): number => {
  const quantity = num(line?.quantity)
  return quantity > 0 ? lineTaxAmount(line, base) / quantity : 0
}
